/* Sticker Select — size estimator + PDF/SVG previews.
 * Shows the customer roughly how big their finished sticker will be: the design is scaled
 * (never stretched) to fit inside the chosen size. Transparent edges of PNG/WebP/GIF files are
 * ignored, because that is where a die cut line goes. Display + order note only — never affects price.
 */
(() => {
  'use strict';
  const fmtIn = (n) => `${Number(n).toString()}"`;
  const MEASURABLE = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'pdf'];
  const PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/';
  let pdfjsReady = null;
  const loadPdfJs = () => pdfjsReady || (pdfjsReady = new Promise((res, rej) => {
    if (window.pdfjsLib) return res(window.pdfjsLib);
    const sc = document.createElement('script'); sc.src = PDFJS + 'pdf.min.js'; sc.async = true;
    sc.onload = () => { try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS + 'pdf.worker.min.js'; res(window.pdfjsLib); } catch (e) { rej(e); } };
    sc.onerror = rej; document.head.appendChild(sc);
  }));
  /** Render page 1 of a PDF in the browser (transparent background) so customers can see their file. */
  async function pdfPreview(file) {
    const lib = await loadPdfJs();
    const pdf = await lib.getDocument({ data: await file.arrayBuffer() }).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(4, 1400 / Math.max(base.width, base.height));
    const vp = page.getViewport({ scale });
    const c = document.createElement('canvas'); c.width = Math.round(vp.width); c.height = Math.round(vp.height);
    await page.render({ canvasContext: c.getContext('2d'), viewport: vp, background: 'rgba(0,0,0,0)' }).promise;
    const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
    try { pdf.destroy(); } catch (e) { /* ignore */ }
    return blob ? URL.createObjectURL(blob) : null;
  }
  const FIXED_SHAPES = ['circle', 'square', 'rectangle', 'oval', 'sticker sheet'];

  const loadImg = (url) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });

  async function measure(state) {
    if (!state || !state.previewUrl || !MEASURABLE.includes(state.ext || '')) return null;
    const img = await loadImg(state.previewUrl);
    let w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) { if (state.ext !== 'svg') return null; w = h = 1000; }
    const k = Math.min(1, 400 / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * k)), ch = Math.max(1, Math.round(h * k));
    const c = document.createElement('canvas'); c.width = cw; c.height = ch;
    const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(img, 0, 0, cw, ch);
    const d = ctx.getImageData(0, 0, cw, ch).data;
    // Background = transparent pixels, plus a solid background colour when all four corners share it
    // (e.g. a logo on a white JPG or a white PDF page).
    const px = (x, y) => { const i = (y * cw + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; };
    const corners = [px(0, 0), px(cw - 1, 0), px(0, ch - 1), px(cw - 1, ch - 1)];
    const near = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) <= 60;
    const solidBg = corners.every((p) => p[3] > 200 && near(p, corners[0])) ? corners[0] : null;
    let minX = cw, minY = ch, maxX = -1, maxY = -1;
    for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) {
      const i = (y * cw + x) * 4;
      if (d[i + 3] <= 16) continue;
      if (solidBg && Math.abs(d[i] - solidBg[0]) + Math.abs(d[i + 1] - solidBg[1]) + Math.abs(d[i + 2] - solidBg[2]) <= 60) continue;
      if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (maxX < 0) return { w, h, trimmed: false };
    const bw = (maxX - minX + 1) / k, bh = (maxY - minY + 1) / k;
    if (bw >= w * 0.98 && bh >= h * 0.98) return { w, h, trimmed: false };
    return { w: bw, h: bh, trimmed: true, bg: solidBg ? 'solid' : 'transparent' };
  }

  customElements.whenDefined('product-configurator').then(() => {
    const proto = customElements.get('product-configurator').prototype;
    if (proto.updateEstimate) return;

    proto.sizeEstimate = function () {
      const dims = this.sizeDims ? this.sizeDims() : null;
      const s = this.upload ? this.upload.state : null;
      if (!dims || !s || s.status === 'empty' || s.status === 'error') return null;
      const cut = String(this.dataset.choiceCutType || '');
      const fixed = FIXED_SHAPES.includes(cut);
      const sel = `${fmtIn(dims.w)} × ${fmtIn(dims.h)}`;
      const m = this._ssMeasure;
      if (!m) return { known: false, fixed, sel, dims, cut };
      const r1 = (n) => Math.round(n * 10) / 10;
      const k = Math.min(dims.w / m.w, dims.h / m.h);
      const w = r1(m.w * k), h = r1(m.h * k);
      return { known: true, fixed, sel, dims, cut, w, h, trimmed: m.trimmed, bg: m.bg, exact: Math.abs(w - dims.w) < 0.05 && Math.abs(h - dims.h) < 0.05 };
    };

    proto.updateEstimate = function () {
      const grid = this.querySelector('.opt-grid--size');
      if (!grid) return;
      let box = this.querySelector('[data-size-estimate]');
      if (!box) {
        box = document.createElement('div');
        box.className = 'size-estimate'; box.setAttribute('data-size-estimate', ''); box.setAttribute('aria-live', 'polite');
        box.style.cssText = 'display:flex;gap:.85rem;align-items:center;margin-top:.85rem;padding:.75rem .9rem;border:1px solid var(--c-line);border-radius:var(--radius-sm);background:var(--c-surface)';
        grid.insertAdjacentElement('afterend', box);
      }
      const e = this.sizeEstimate();
      if (!e) { box.hidden = true; box.style.display = 'none'; return; }
      const sq = 52, max = Math.max(e.dims.w, e.dims.h);
      const ow = Math.round((e.dims.w / max) * sq), oh = Math.round((e.dims.h / max) * sq);
      const round = e.cut === 'circle' || e.cut === 'oval' ? '50%' : '6px';
      let inner = '', text;
      if (e.known) {
        const iw = Math.max(4, Math.round((e.w / max) * sq)), ih = Math.max(4, Math.round((e.h / max) * sq));
        inner = `<span style="width:${iw}px;height:${ih}px;background:var(--c-accent);border-radius:3px;display:block"></span>`;
        const est = `${fmtIn(e.w)} × ${fmtIn(e.h)}`;
        if (e.fixed) text = `<strong>Your sticker: ${e.sel}</strong><br><span class="muted">Your design fits inside at about ${est}.</span>`;
        else if (e.exact) text = `<strong>Estimated sticker size: ${est}</strong><br><span class="muted">A match for the ${e.sel} you picked.</span>`;
        else text = `<strong>Estimated sticker size: about ${est}</strong><br><span class="muted">Your design keeps its proportions, so it fits within the ${e.sel} you picked${e.trimmed ? (e.bg === 'solid' ? ' (background trimmed)' : ' (transparent edges trimmed)') : ''}.</span>`;
      } else {
        text = `<strong>Sticker size: up to ${e.sel}</strong><br><span class="muted">We'll measure your file and confirm the exact size on your proof.</span>`;
      }
      box.innerHTML = `<span aria-hidden="true" style="flex:0 0 ${sq}px;height:${sq}px;display:flex;align-items:center;justify-content:center"><span style="width:${ow}px;height:${oh}px;border:2px dashed var(--c-ink);border-radius:${round};display:flex;align-items:center;justify-content:center">${inner}</span></span><span style="font-size:.92rem;line-height:1.4">${text}</span>`;
      box.hidden = false; box.style.display = 'flex';
    };

    const origUpdate = proto.update;
    proto.update = function (...args) {
      const r = origUpdate.apply(this, args);
      try { this.updateEstimate(); } catch (err) { /* never block the configurator */ }
      return r;
    };
    const origBuild = proto.buildProperties;
    proto.buildProperties = function (...args) {
      const p = origBuild.apply(this, args);
      try {
        const e = this.sizeEstimate();
        if (e && e.known) p['Estimated size'] = e.fixed ? `${e.sel} (design ≈ ${fmtIn(e.w)} × ${fmtIn(e.h)})` : `≈ ${fmtIn(e.w)} × ${fmtIn(e.h)}`;
      } catch (err) { /* ignore */ }
      return p;
    };

    const hook = (el) => {
      if (el._ssEst) return; el._ssEst = true;
      // PDFs: the uploader can't preview them, so render page 1 here and hand it back as the preview image.
      const previewPdf = async (file) => {
        if (!file || !/\.pdf$/i.test(file.name) || !el.upload) return;
        try {
          const url = await pdfPreview(file);
          const s = el.upload.state;
          if (!url || !s || s.status === 'empty' || s.size !== file.size || s.previewUrl) { if (url) URL.revokeObjectURL(url); return; }
          s.previewUrl = url;
          el.upload.render();
          el.upload.dispatchEvent(new CustomEvent('artwork:change', { bubbles: true, detail: s }));
        } catch (err) { /* preview is optional — the upload itself is unaffected */ }
      };
      const input = el.querySelector('[data-file-input]');
      if (input) input.addEventListener('change', () => previewPdf(input.files && input.files[0]));
      const drop = el.querySelector('[data-drop]');
      if (drop) drop.addEventListener('drop', (ev) => previewPdf(ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0]));
      el.addEventListener('artwork:change', async () => {
        const s = el.upload && el.upload.state;
        const key = s && s.previewUrl;
        if (!key) { el._ssKey = null; el._ssMeasure = null; }
        else if (el._ssKey !== key) {
          el._ssKey = key; el._ssMeasure = null;
          try { const m = await measure(s); if (el._ssKey === key) el._ssMeasure = m; } catch (err) { /* ignore */ }
        }
        el.updateEstimate();
      });
      el.updateEstimate();
    };
    document.querySelectorAll('product-configurator').forEach(hook);
  });
})();
